/**
 * Dataspace Tree Store - Global filesystem tree for entire dataspace
 * 
 * Data Model: Edge documents + fractional indexing (same as spaceTreeStore)
 * - Each edge: (dataspace_id, parent_id, child_id, pos, expanded)
 * - Edges stored in separate collection (not on entity.skeleton)
 * - Supports aliases (same entity can have multiple edges)
 * 
 * Key Differences from SpaceTreeStore:
 * - ✅ No spaceId filtering - shows ALL entities in dataspace
 * - ✅ Persists to dataspace_edges API (not entity.skeleton)
 * - ✅ Supports aliases (entity in multiple locations)
 * - ✅ User-agnostic (shared tree for entire dataspace/org)
 * 
 * Architecture:
 * - ✅ Optimistic updates (instant UI feedback)
 * - ✅ Operation log for persistence (debounced flush)
 * - ✅ Subscribes to entityRegistry for reactive node creation/deletion
 */

import { create } from 'zustand';
import { BaseEntityType } from '../types/entities';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import { entityRegistry } from '../stores/entityRegistry';
import { generatePositionBetween, generateSpacedPositions } from './treePosition';
import { isGenericTreeHiddenType } from '../utils/genericTreeHiddenTypes';

const log = createFileLogger('DATASPACE_TREE_STORE', { enabled: false });
const VERBOSE_LOGGING = false;

const TREE_STORE_VERSION = 'v2.0-alias-edges'; // ✅ Changed: Now supports aliases with edgeKey format
log.log('🌲 [DataspaceTreeStore] Loaded version:', TREE_STORE_VERSION);

// ✅ MIGRATION: Force clear tree on version change
const STORED_VERSION_KEY = 'dataspaceTreeStore_version';
const storedVersion = localStorage.getItem(STORED_VERSION_KEY);
if (storedVersion !== TREE_STORE_VERSION) {
  log.log('🔄 [DataspaceTreeStore] Version changed from', storedVersion, 'to', TREE_STORE_VERSION);
  log.log('🔄 [DataspaceTreeStore] Clearing old tree data and forcing refresh');
  localStorage.setItem(STORED_VERSION_KEY, TREE_STORE_VERSION);
  // Set flag to force refresh on next initialize()
  (window as any).__forceDataspaceTreeRefresh = true;
  
  // ✅ CRITICAL: Also immediately reset the store state to clear old format data
  // This ensures any already-created store instance gets cleared
  setTimeout(() => {
    const store = useDataspaceTreeStore.getState();
    if (Object.keys(store.nodesById).length > 0) {
      log.log('🔄 [DataspaceTreeStore] Clearing', Object.keys(store.nodesById).length, 'old format nodes');
      // Capture dataspaceId before clearing — if initialize() already ran (race condition),
      // we must re-run it after clearing since the component's useEffect won't re-fire.
      const prevDataspaceId = store.dataspaceId;
      useDataspaceTreeStore.setState({
        nodesById: {},
        childrenByParentId: new Map(),
        initialized: false,
        rebuildCount: 0,
        dataspaceId: null
      });
      if (prevDataspaceId) {
        log.log('🔄 [DataspaceTreeStore] Re-initializing after version migration clear:', prevDataspaceId);
        useDataspaceTreeStore.getState().initialize(prevDataspaceId);
      }
    }
  }, 0);
}

// ========================================================================
// Tree Node & Edge Structures
// ========================================================================

interface TreeNode {
  id: string; // Entity ID
  parentId: string | null;
  pos: string;
  expanded: boolean;
  kind?: 'canonical' | 'alias';
}

// ✅ Helper to create unique edge key for alias support
// EXPORTED for use in other components
export function getEdgeKey(entityId: string, parentId: string | null): string {
  return `${parentId || 'ROOT'}:${entityId}`;
}

/**
 * Partition a full edge list into the transparent-root layer (always mounted)
 * vs nested Group children (lazy-deferred).
 *
 * Transparent root (Drive My Drive / org filesystem) hoists Space children, so
 * those edges MUST be mounted during REST-FALLBACK — they are never user-
 * expanded. Only descendants inside Groups stay in pendingFullEdges.
 */
export function partitionEdgesForTransparentRoot<T extends { parentId: string | null; childId: string }>(
  allEdges: T[],
  options: {
    dataspaceId: string;
    /** Entity IDs known to be Space (desktop) entities. */
    spaceIds: ReadonlySet<string>;
  },
): { mount: T[]; deferred: T[]; loadedParents: Set<string> } {
  const { dataspaceId, spaceIds } = options;
  const orgRootParents = new Set<string | null>([
    null,
    dataspaceId,
    `org:${dataspaceId}`,
  ]);

  const mount: T[] = [];
  const deferred: T[] = [];
  const loadedParents = new Set<string>(['ROOT']);

  for (const edge of allEdges) {
    const parentId = edge.parentId;
    const isTransparentRootLayer =
      orgRootParents.has(parentId) ||
      (parentId !== null && spaceIds.has(parentId));

    if (isTransparentRootLayer) {
      mount.push(edge);
      if (parentId !== null) loadedParents.add(parentId);
    } else {
      deferred.push(edge);
    }
  }

  return { mount, deferred, loadedParents };
}

/** Collect Space entity IDs from REST node metadata and/or the live registry. */
export function collectSpaceIdsFromNodeMeta(
  nodes: Record<string, { entityType?: string } | null | undefined> | null | undefined,
): Set<string> {
  const spaceIds = new Set<string>();
  if (nodes) {
    for (const [id, meta] of Object.entries(nodes)) {
      if (meta?.entityType === 'Space') spaceIds.add(id);
    }
  }
  try {
    const all = entityRegistry.dumpAll().flatMap((__s) => __s.entities) as Array<{ _id?: string; entityType?: string }>;
    for (const entity of all) {
      if (entity?._id && entity.entityType === 'Space') spaceIds.add(entity._id);
    }
  } catch {
    // Registry may be unavailable in unit tests — nodes alone are enough.
  }
  return spaceIds;
}

interface DataspaceEdge {
  id: string;
  dataspaceId: string;
  parentId: string | null;
  childId: string;
  pos: string;
  expanded: boolean;
  kind: 'canonical' | 'alias';
  createdAt: string;
  updatedAt: string;
}

// TreeOperation is kept for potential future use but no longer used for persistence.
type TreeOperation = 
  | { type: 'move'; nodeId: string; parentId: string | null; pos: string; timestamp: number }
  | { type: 'expand'; nodeId: string; expanded: boolean; timestamp: number }
  | { type: 'create'; nodeId: string; parentId: string | null; pos: string; kind: 'canonical' | 'alias'; timestamp: number }
  | { type: 'delete'; nodeId?: string; parentId?: string | null; timestamp: number };

// ========================================================================
// Per-user expanded state (localStorage, not shared via CRDT)
// ========================================================================

const _EXPANDED_LS_KEY = 'dataspaceTree_expanded';

function _loadExpandedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(_EXPANDED_LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

function _saveExpandedSet(expanded: Set<string>): void {
  try {
    localStorage.setItem(_EXPANDED_LS_KEY, JSON.stringify([...expanded]));
  } catch { /* quota exceeded — non-fatal */ }
}

const _expandedNodes = _loadExpandedSet();

function _isExpandedLocal(nodeId: string): boolean {
  return _expandedNodes.has(nodeId);
}

function _setExpandedLocal(nodeId: string, expanded: boolean): void {
  if (expanded) _expandedNodes.add(nodeId);
  else _expandedNodes.delete(nodeId);
  _saveExpandedSet(_expandedNodes);
}

// ========================================================================
// Org reseed — debounced, non-destructive correction for Y.Map corruption
// ========================================================================

// When the cycle-breaker fires, we ask the backend to clear and re-seed the
// org Y.Doc from authoritative MongoDB rather than guessing which edge to evict.
let _reseedTimer: ReturnType<typeof setTimeout> | null = null;
const _RESEED_DEBOUNCE_MS = 3000;

function _scheduleOrgReseed(dataspaceId: string | null): void {
  if (!dataspaceId) return;
  if (_reseedTimer) clearTimeout(_reseedTimer);
  _reseedTimer = setTimeout(() => {
    _reseedTimer = null;
    fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/reseed-tree`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
      .catch((err: unknown) => {
        console.warn('[DataspaceTreeStore] Org reseed request failed:', err);
      });
  }, _RESEED_DEBOUNCE_MS);
}

// ========================================================================
// Store State
// ========================================================================

interface DataspaceTreeState {
  dataspaceId: string | null;
  nodesById: Record<string, TreeNode>; // Keyed by edgeKey "(parentId or 'ROOT'):childId"
  childrenByParentId: Map<string | null, string[]>;
  initialized: boolean;
  rebuildCount: number;
  /** Cleanup handle for the entityRegistry.subscribeToChanges listener. */
  entityRegistryUnsubscribe: (() => void) | null;
  entityEventUnsubscribe: (() => void) | null;
  /**
   * False only when the tree was built via the REST-FALLBACK path (org Y.Doc
   * didn't sync in time) — in that case the initial load fetches root-level
   * edges only, and folders lazily fetch their children on first expand (see
   * ensureFolderChildrenLoaded). The Y.Doc path always yields the complete
   * tree in one shot (a CRDT snapshot can't be partially synced), so this
   * stays true for the overwhelmingly common case and lazy-loading never
   * engages there.
   */
  treeFullyLoaded: boolean;
  /** parentId ('ROOT' sentinel for null) → children already merged in from pendingFullEdges. */
  loadedChildrenParents: Set<string>;
  /**
   * Non-root edges from the REST-fallback fetch that haven't been merged into
   * nodesById yet. The fallback fetch already retrieves the whole tree in one
   * request (it's shared with the node-metadata seed below), so lazy-loading
   * here saves the upfront cost of building/rendering thousands of off-screen
   * TreeNode entries and their sidebar rows, not a second network round trip.
   */
  pendingFullEdges: DataspaceEdge[] | null;
}

interface DataspaceTreeStore extends DataspaceTreeState {
  initialize: (dataspaceId: string) => Promise<void>;
  initFromEdges: (edges: DataspaceEdge[]) => void;

  /**
   * Applies the move optimistically and persists it to the backend.
   * Resolves `true` once the backend confirms the move, `false` if the node
   * couldn't be found or the backend call failed (in which case the optimistic
   * update was already rolled back internally). Never rejects — callers that
   * need to react to failure (e.g. rolling back their own optimistic state)
   * should check the resolved value rather than try/catch.
   */
  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => Promise<boolean>;
  moveNodes: (inputs: Array<{
    childId: string
    newParentId: string | null
    afterNodeId: string | null
    fromParentId?: string | null
  }>) => Promise<boolean>;
  toggleExpanded: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  /**
   * No-ops unless the tree is in REST-fallback (partial) mode. Otherwise
   * lazily fetches and merges in `nodeId`'s children the first time it's
   * expanded, caching the result so re-expanding never re-fetches.
   */
  ensureFolderChildrenLoaded: (nodeId: string) => Promise<void>;
  /**
   * Merge a node's own deferred edge and every deferred ancestor edge above
   * it into the live tree. `ensureFolderChildrenLoaded` only lazily loads a
   * folder's *children* — a folder's own edge (and its ancestors') can still
   * be sitting in `pendingFullEdges` when the user deep-links directly into a
   * nested folder without having opened its parents first. No-op once fully
   * loaded or once the chain reaches an already-mounted node.
   */
  ensureNodeChainLoaded: (nodeId: string) => void;
  expandAncestors: (nodeId: string) => void;
  expandAllNodes: () => { count: number };
  collapseAllNodes: () => { count: number };
  ensureNodeExists: (
    nodeId: string,
    parentId: string | null,
    kind?: 'canonical' | 'alias',
    options?: { skipBackendPersist?: boolean }
  ) => void;
  addAliasEdge: (entityId: string, parentId: string | null) => Promise<void>;

  getRootNodeIds: () => string[];
  getChildNodeIds: (parentId: string | null) => string[];
  /**
   * Returns ALL descendant entity IDs (any depth) under parentId.
   * This is the single frontend traversal for folder-delete optimistic UI.
   * Cycle-guarded via a visited set so corrupt circular edges cannot loop.
   * Does NOT include parentId itself.
   */
  getDescendantNodeIds: (parentId: string) => string[];
  getNode: (nodeId: string, parentId?: string | null) => TreeNode | undefined;
  isExpanded: (nodeId: string, parentId?: string | null) => boolean;
  hasChildren: (nodeId: string) => boolean;
  isEntityInSubtree: (entityId: string, ancestorId: string) => boolean;

  _rebuildChildrenMap: () => void;
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => void;
  _applyRemoveNode: (edgeKey: string) => void;
  _applyRemoveNodes: (edgeKeys: string[]) => void;
  _restoreRemovedNodes: (entries: Array<[string, TreeNode]>) => void;
  _applyEdgeBatch: (adds: [string, TreeNode][], removes: string[]) => void;

  cleanup: () => void;
  debugPrintTree: () => void;
}

// ========================================================================
// Helper: Filter displayable entities
// ========================================================================

function isDisplayableEntity(entity: BaseEntityType): boolean {
  if ((entity as any).hidden === true) return false;
  if ((entity as any).trashed === true) return false;
  // Delegates to the same shared genericTreeHiddenTypes core Drive and the
  // sidebar use for generic-list rendering (see gui/src/utils/genericTreeHiddenTypes.ts).
  // This function's only remaining caller is an observability log on entity
  // creation — not the actual tree-rendering path (real filtering happens via
  // dataspace_edges) — so it just needs to agree with real display behavior
  // instead of maintaining its own, fourth, independently-drifting definition.
  return !isGenericTreeHiddenType(entity.entityType);
}

// ========================================================================
// Store Implementation
// ========================================================================

export const useDataspaceTreeStore = create<DataspaceTreeStore>((set, get) => ({
  // Initial state
  dataspaceId: null,
  nodesById: {},
  childrenByParentId: new Map(),
  initialized: false,
  rebuildCount: 0,
  entityRegistryUnsubscribe: null,
  entityEventUnsubscribe: null,
  treeFullyLoaded: true,
  loadedChildrenParents: new Set(),
  pendingFullEdges: null,
  
  // ========================================================================
  // Initialization
  // ========================================================================
  
  initialize: async (dataspaceId: string) => {
    const { initialized: currentlyInit, dataspaceId: currentDataspaceId, entityRegistryUnsubscribe, entityEventUnsubscribe } = get();

    log.log('[DataspaceTreeStore] 🚀 Initializing for dataspace:', dataspaceId);

    // Cleanup previous subscriptions if switching dataspaces
    if (currentlyInit && currentDataspaceId !== dataspaceId) {
      if (entityRegistryUnsubscribe) {
        entityRegistryUnsubscribe();
        set({ entityRegistryUnsubscribe: null });
      }
      if (entityEventUnsubscribe) {
        entityEventUnsubscribe();
        set({ entityEventUnsubscribe: null });
      }
    }

    // Check for version migration flag or old key format
    const forceRefresh = (window as any).__forceDataspaceTreeRefresh;

    if (currentlyInit && currentDataspaceId === dataspaceId) {
      log.log('[DataspaceTreeStore] Already initialized for this dataspace');

      if (forceRefresh) {
        log.log('[DataspaceTreeStore] 🔄 Version migration - forcing refresh');
        (window as any).__forceDataspaceTreeRefresh = false;
        set({ nodesById: {}, childrenByParentId: new Map(), initialized: false, rebuildCount: 0 });
      } else {
        const { nodesById } = get();
        const firstKey = Object.keys(nodesById)[0];
        const usesOldFormat = firstKey && !firstKey.includes(':');
        if (usesOldFormat) {
          log.log('[DataspaceTreeStore] ⚠️ Old key format detected, forcing refresh');
          set({ nodesById: {}, childrenByParentId: new Map(), initialized: false, rebuildCount: 0 });
        } else {
          return;
        }
      }
    }

    set({
      dataspaceId,
      initialized: false,
      nodesById: {},
      childrenByParentId: new Map(),
      rebuildCount: 0,
      treeFullyLoaded: true,
      loadedChildrenParents: new Set(),
      pendingFullEdges: null,
    });
    log.log('[TREE_INIT] 🌲 initialize() called with dataspaceId:', dataspaceId);

    try {
      // Build tree from org Y.Doc (seeded from MongoDB on cold start, kept current by backend broadcasts).
      // The org WS is the canonical live source, but it can be slow / disconnected /
      // circuit-open for users with bloated client Y.Docs. We kick off REST in parallel
      // and short-circuit to whichever returns first, so the sidebar always renders
      // within ~2s even when the WS is unreachable. _applyFullTreeSync is merge-aware
      // when the tree is already initialized (orgYDocStore._applyFullTreeSync), so a
      // late-arriving Y.Doc sync gracefully merges into the REST-built tree.
      const { waitForOrgSync, getOrgEdgesSnapshot, setRestNodesFallback } = await import('./orgYDocStore');

      let edges: DataspaceEdge[] = [];
      let source = 'Y.Doc';
      const initStart = Date.now();

      // Kick off REST in parallel so it's already in flight when we fall through.
      // include_nodes=true also returns {name, icon, entityType} for every live child —
      // the same metadata seeded into the org Y.Doc `nodes` map. We feed that into
      // orgYDocStore's REST fallback unconditionally (below), independent of whether
      // Y.Doc or REST ends up winning the edges race, so node metadata (needed to hoist
      // Desktops and render entity stubs in the global Drive view) is never solely
      // dependent on WS health.
      const restPromise = fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges?include_nodes=true`, { credentials: 'include' })
        .then((response: any) => {
          if (response && Array.isArray(response.edges)) {
            if (response.nodes) setRestNodesFallback(dataspaceId, response.nodes);
            return {
              edges: response.edges as DataspaceEdge[],
              nodes: (response.nodes ?? null) as Record<string, { entityType?: string }> | null,
            };
          }
          log.warn('[TreeInit] Unexpected REST edges response shape:', response);
          return null;
        })
        .catch((err) => { log.warn('[TreeInit] REST edges fetch failed:', err); return null; });

      // Short Y.Doc timeout: prefer live data if it syncs fast, otherwise REST wins.
      // 800ms after Phase-1+2 fixes (correct room + early waiter resolution on WS sync),
      // the org Y.Doc typically resolves in <300ms for new users. REST is already in
      // flight in parallel so the fallback adds minimal latency.
      const Y_DOC_TIMEOUT_MS = 800;

      try {
        await waitForOrgSync(Y_DOC_TIMEOUT_MS);
        const snapshot = getOrgEdgesSnapshot();
        edges = snapshot.map(e => ({
          id: e.edgeId,
          dataspaceId,
          parentId: e.parentId,
          childId: e.childId,
          pos: e.pos,
          expanded: e.expanded,
          kind: e.kind,
          createdAt: '',
          updatedAt: '',
        }));
        log.log(`[TreeInit] ✅ Source: Y.Doc | edges: ${edges.length} | time: ${Date.now() - initStart}ms`);
      } catch {
        // Y.Doc didn't sync in time (WS slow, disconnected, or circuit-open) — use REST.
        log.warn(`[DataspaceTreeStore] ⚠️ Y.Doc sync >${Y_DOC_TIMEOUT_MS}ms — using REST`);
        source = 'REST-FALLBACK';
        const response = await restPromise;
        if (!response || !Array.isArray(response.edges)) {
          throw new Error(`Expected edge array, got ${typeof response?.edges}`);
        }

        // Transparent-root layer (null + org-root + Space children) must mount
        // immediately — Drive/sidebar hoist Space children and never expand
        // Spaces. Only nested Group children stay deferred for lazy expand.
        const spaceIds = collectSpaceIdsFromNodeMeta(response.nodes);
        const { mount, deferred, loadedParents } = partitionEdgesForTransparentRoot(
          response.edges,
          { dataspaceId, spaceIds },
        );
        edges = mount;
        set({
          treeFullyLoaded: deferred.length === 0,
          pendingFullEdges: deferred.length > 0 ? deferred : null,
          loadedChildrenParents: loadedParents,
        });
        log.log(
          `[TreeInit] ⚠️ Source: REST-FALLBACK | root-layer edges: ${mount.length} ` +
          `| deferred: ${deferred.length} | spaces: ${spaceIds.size} | time: ${Date.now() - initStart}ms`,
        );
      }

      get().initFromEdges(edges);

      const treeNodeCount = Object.keys(get().nodesById).length;
      log.log(`[TreeInit] 🌳 Tree built: ${treeNodeCount} nodes from ${source}`);
      log.log(`[DataspaceTreeStore] Y.Map key format: semantic (parentId:childId) | sample: ${edges[0]?.id ?? 'n/a'}`);

      // Subscribe to entity deletions — removed entities must be pruned from the tree.
      // New entities arrive via the org Y.Doc edge observer (_handleEdgeChanges), not here.
      //
      // O(1) deletion detection: compare entitiesById Maps instead of O(n²) Array.find().
      // Build a reverse index (entityId → edgeKey[]) once per subscription fire so we
      // can look up which edges to remove in O(1) rather than scanning all edges per deletion.
      // O(1) deletion detection: compare prev vs current entity-id snapshots
      // assembled from the registry. The registry's `subscribeToChanges`
      // listener fires after every mutation across all backing stores.
      let prevIdSet = new Set<string>(
        (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).map((e: any) => e._id).filter(Boolean),
      );
      const unsubscribe = entityRegistry.subscribeToChanges(() => {
        const { dataspaceId: currentDs } = get();
        if (!currentDs) return;

        const allCurrent = entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[];

        // Guard: bulk reload clears then repopulates — skip to avoid mass edge wipe
        if (allCurrent.length === 0 && prevIdSet.size > 0) {
          log.warn('[DataspaceTreeStore] entity registry cleared — skipping deletion detection');
          prevIdSet = new Set();
          return;
        }

        const currentIdSet = new Set<string>(allCurrent.map((e: any) => e._id).filter(Boolean));
        const deletedEntityIds: string[] = [];
        prevIdSet.forEach((id) => {
          if (!currentIdSet.has(id)) deletedEntityIds.push(id);
        });
        prevIdSet = currentIdSet;

        if (deletedEntityIds.length > 0) {
          log.log('[DataspaceTreeStore] 🗑️ Detected', deletedEntityIds.length, 'deleted entities');
          const deletedSet = new Set(deletedEntityIds);
          const currentNodesById = get().nodesById;
          // Build reverse index: entityId → edgeKeys (most entities have 1 edge)
          const edgesByEntityId = new Map<string, string[]>();
          Object.entries(currentNodesById).forEach(([edgeKey, node]) => {
            if (deletedSet.has(node.id)) {
              const keys = edgesByEntityId.get(node.id) || [];
              keys.push(edgeKey);
              edgesByEntityId.set(node.id, keys);
            }
          });
          edgesByEntityId.forEach((edgeKeys) => {
            edgeKeys.forEach(edgeKey => get()._applyRemoveNode(edgeKey));
          });
        }
      });

      log.log(`[TREE_INIT] ✅ initialized=true — tree is ready. Nodes: ${Object.keys(get().nodesById).length}`);
      set({ initialized: true, entityRegistryUnsubscribe: unsubscribe });

      // Subscribe to entity creation events for observability (edges arrive via Y.Doc observer)
      (async () => {
        try {
          const { entityEventService } = await import('../services/entityEventService');
          const eventUnsubscribe = entityEventService.onEntityEvent('created', (event) => {
            const { initialized: isInit } = get();
            if (!isInit || !isDisplayableEntity(event.entity)) return;
            log.log('[DataspaceTreeStore] 📥 Entity created:', event.entity.entityType, event.entity.name);
          });
          set({ entityEventUnsubscribe: eventUnsubscribe });
          log.log('[DataspaceTreeStore] ✅ Subscribed to entity creation events');
        } catch (error) {
          log.error('[DataspaceTreeStore] Failed to subscribe to entity events:', error);
        }
      })();

      // POST-INIT RECONCILIATION: After 3 seconds, run a convergent diff to catch
      // any edges that were missed or created while the WS was reconnecting.
      //
      // Source of truth: the org Y.Doc snapshot (not REST). MongoDB/REST lags the
      // Y.Doc after a move, so converging toward REST would delete freshly-moved
      // edges. The Y.Doc already contains all live writes (local and remote).
      //
      // When the org Y.Doc did not sync (WS circuit-open / offline), the tree was
      // built from REST in the init path above; the reconciliation is skipped so a
      // stale Y.Doc doesn't corrupt a good REST-built tree.
      const reconcileDs = dataspaceId;
      setTimeout(async () => {
        const { dataspaceId: currentDs, initialized: isStillInit } = get();
        if (!isStillInit || currentDs !== reconcileDs) return;

        try {
          const { isOrgSynced, getOrgEdgesSnapshot } = await import('./orgYDocStore');

          if (!isOrgSynced()) {
            // WS never synced — the tree was built from REST. Skip the convergent diff
            // to avoid clobbering a valid REST-built tree with an empty/stale Y.Doc.
            log.log('[TreeInit] Post-init reconciliation skipped — org Y.Doc not synced (WS offline)');
            return;
          }

          const snapshot = getOrgEdgesSnapshot();
          const snapshotKeys = new Set(snapshot.map((e) => e.edgeId));

          const { nodesById: currentNodes, treeFullyLoaded, pendingFullEdges } = get();
          const treeKeys = new Set(Object.keys(currentNodes));

          // Edges in the Y.Doc snapshot that are missing from the local tree.
          const toAdd = snapshot.filter((e) => !treeKeys.has(e.edgeId));
          // Edges in the local tree that have been removed from the Y.Doc.
          let toRemove = Object.keys(currentNodes).filter((k) => !snapshotKeys.has(k));

          // While nested Group children are still deferred, never strip local
          // edges against a possibly-incomplete Y.Doc — that is how a partial
          // REST root layer can be wiped into an empty My Drive.
          if (!treeFullyLoaded && pendingFullEdges && pendingFullEdges.length > 0) {
            if (toRemove.length > 0) {
              log.log(
                `[TreeInit] Post-init reconciliation: skipping ${toRemove.length} removes ` +
                `while ${pendingFullEdges.length} edges remain deferred`,
              );
            }
            toRemove = [];
          }

          if (toAdd.length === 0 && toRemove.length === 0) {
            log.log('[TreeInit] ✅ Post-init reconciliation: tree is consistent with org Y.Doc');
            return;
          }

          console.warn(
            `[TreeInit] POST-INIT RECONCILIATION (Y.Doc): +${toAdd.length} add, -${toRemove.length} remove`,
          );

          toAdd.forEach((e) => {
            get()._applyAddNode(e.edgeId, {
              id: e.childId,
              parentId: e.parentId,
              pos: e.pos || 'M',
              expanded: e.expanded ?? false,
              kind: e.kind ?? 'canonical',
            });
          });

          toRemove.forEach((edgeKey) => {
            get()._applyRemoveNode(edgeKey);
          });

          if (toAdd.length > 0 || toRemove.length > 0) {
            get()._rebuildChildrenMap();
          }
        } catch (err) {
          log.warn('[TreeInit] Post-init reconciliation failed (non-critical):', err);
        }
      }, 3000);

      log.log('[DataspaceTreeStore] ✅ Initialization complete');

    } catch (error) {
      log.error('[TREE_INIT] ❌ Initialization FAILED:', error);
      set({ initialized: false });
      setTimeout(() => {
        const { dataspaceId: currentId, initialized: isInit } = get();
        if (!isInit && currentId === dataspaceId) {
          log.log('[DataspaceTreeStore] 🔄 Retrying initialization for:', dataspaceId);
          get().initialize(dataspaceId);
        }
      }, 3000);
    }
  },
  
  initFromEdges: (edges: DataspaceEdge[]) => {
    log.log('[DataspaceTreeStore] 🔨 Building tree from', edges.length, 'edges');

    // Filter out trashed entities (their edges are stale).
    const trashedEntityIds = new Set<string>();
    (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).forEach((e: any) => {
      if (e.trashed === true && e._id) trashedEntityIds.add(e._id);
    });
    const validEdges = edges.filter((edge: DataspaceEdge) => !trashedEntityIds.has(edge.childId));
    if (validEdges.length < edges.length) {
      log.warn('[DataspaceTreeStore] ⚠️ Filtered out', edges.length - validEdges.length, 'trashed-entity edges');
    }

    // Deduplicate canonical edges per child (safety net for legacy data; should be no-ops with Y.Map keys)
    const edgesByChild = new Map<string, DataspaceEdge[]>();
    validEdges.forEach((edge: DataspaceEdge) => {
      const existing = edgesByChild.get(edge.childId) || [];
      existing.push(edge);
      edgesByChild.set(edge.childId, existing);
    });

    const deduplicatedEdges: DataspaceEdge[] = [];
    edgesByChild.forEach((entityEdges, childId) => {
      if (entityEdges.length === 1) {
        deduplicatedEdges.push(entityEdges[0]);
        return;
      }
      const canonicalEdges = entityEdges.filter(e => e.kind !== 'alias');
      const aliasEdges = entityEdges.filter(e => e.kind === 'alias');
      if (canonicalEdges.length > 1) {
        const sorted = canonicalEdges.sort((a, b) => {
          if (a.parentId !== null && b.parentId === null) return -1;
          if (a.parentId === null && b.parentId !== null) return 1;
          const aIsOld = typeof a.pos === 'string' && /^M+$/.test(a.pos);
          const bIsOld = typeof b.pos === 'string' && /^M+$/.test(b.pos);
          if (!aIsOld && bIsOld) return -1;
          if (aIsOld && !bIsOld) return 1;
          return (b.id || '').localeCompare(a.id || '');
        });
        deduplicatedEdges.push(sorted[0]);
        log.warn('[DataspaceTreeStore] 🔁 Deduped', canonicalEdges.length, 'edges for', childId);
      } else {
        deduplicatedEdges.push(...canonicalEdges);
      }
      if (aliasEdges.length > 0) deduplicatedEdges.push(...aliasEdges);
    });

    const nodesById: Record<string, TreeNode> = {};
    deduplicatedEdges.forEach((edge: DataspaceEdge) => {
      const edgeKey = getEdgeKey(edge.childId, edge.parentId);
      nodesById[edgeKey] = {
        id: edge.childId,
        parentId: edge.parentId,
        pos: edge.pos,
        expanded: _isExpandedLocal(edge.childId),
        kind: edge.kind,
      };
    });

    log.log('[DataspaceTreeStore] Built', Object.keys(nodesById).length, 'nodes from edges');
    set({ nodesById, rebuildCount: get().rebuildCount + 1 });
    get()._rebuildChildrenMap();
    log.log('[DataspaceTreeStore] ✅ Tree built with', Object.keys(nodesById).length, 'nodes');
  },
  
  // ========================================================================
  // Tree Operations (same API as spaceTreeStore)
  // ========================================================================
  
  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => {
    log.log('[DataspaceTreeStore] 🎯 moveNode:', { nodeId: nodeId.slice(-4), to: newParentId?.slice(-4), afterNodeId: afterNodeId?.slice(-4) });

    // Capture the pre-move state NOW, before the optimistic set() runs, so
    // rollback can faithfully restore the original tree if the API call fails.
    const preMoveSnapshot = { ...get().nodesById };

    let success = false;
    let moveData: { childId: string; oldParentId: string | null; newParentId: string | null; pos: string; expanded: boolean; kind: string } | null = null;
    // Sibling edges whose position changed because of a rebalance. Persisted to the
    // org Y.Doc alongside the moved edge so the new ordering survives a reload.
    const rebalanceWrites: Array<{ childId: string; parentId: string | null; pos: string; expanded: boolean; kind: 'canonical' | 'alias' }> = [];

    set(state => {
      const { nodesById, childrenByParentId, rebuildCount } = state;

      const oldEdgeKey = fromParentId !== undefined
        ? getEdgeKey(nodeId, fromParentId)
        : Object.keys(nodesById).find(key => nodesById[key]?.id === nodeId);

      if (!oldEdgeKey) {
        log.warn('[DataspaceTreeStore] moveNode: node not found:', nodeId);
        log.error('⚠️ Failed to move item - node not found');
        return state;
      }

      const node = nodesById[oldEdgeKey];
      if (!node) {
        log.error('[DataspaceTreeStore] moveNode: edge not found. oldEdgeKey:', oldEdgeKey,
          '| node IDs with this id:', Object.entries(nodesById).filter(([, n]) => n.id === nodeId).map(([k]) => k));
        return state;
      }

      const siblings = (childrenByParentId.get(newParentId) || []).filter(id => id !== nodeId);
      const getSiblingPos = (siblingId: string): string | null =>
        nodesById[getEdgeKey(siblingId, newParentId)]?.pos ?? nodesById[siblingId]?.pos ?? null;

      // Resolve the insertion index among the target siblings. Preserves the prior
      // semantics: a null/unknown afterNodeId inserts before the first sibling,
      // otherwise the node is inserted immediately after afterNodeId.
      const afterIndex = afterNodeId ? siblings.indexOf(afterNodeId) : -1;
      const insertIndex = afterIndex === -1 ? 0 : afterIndex + 1;
      const prevPos = insertIndex > 0 ? getSiblingPos(siblings[insertIndex - 1]) : null;
      const nextPos = insertIndex < siblings.length ? getSiblingPos(siblings[insertIndex]) : null;

      const correctOldEdgeKey = getEdgeKey(nodeId, node.parentId);
      const correctNewEdgeKey = getEdgeKey(nodeId, newParentId);
      const isSameParent = node.parentId === newParentId;

      const newNodesById = { ...nodesById };
      if (oldEdgeKey !== correctOldEdgeKey) delete newNodesById[oldEdgeKey];
      if (!isSameParent) delete newNodesById[correctOldEdgeKey];

      let newPos = generatePositionBetween(prevPos, nextPos);

      // A fractional insert is degenerate when the generated key does not sort
      // strictly between its neighbours — this happens when neighbouring siblings
      // collide (e.g. they share the backend default 'M'). In that case rebalance
      // the whole sibling list to clean, evenly-spaced keys so the dropped item
      // lands exactly where the user placed it instead of snapping to the end.
      const strictlyBetween =
        (prevPos === null || newPos.localeCompare(prevPos) > 0) &&
        (nextPos === null || newPos.localeCompare(nextPos) < 0);

      if (!strictlyBetween) {
        const ordered = [...siblings.slice(0, insertIndex), nodeId, ...siblings.slice(insertIndex)];
        const spaced = generateSpacedPositions(ordered.length);
        ordered.forEach((sibId, idx) => {
          const sibPos = spaced[idx];
          if (sibId === nodeId) {
            newPos = sibPos;
            return;
          }
          const sibKey = getEdgeKey(sibId, newParentId);
          const sibNode = newNodesById[sibKey];
          if (sibNode && sibNode.pos !== sibPos) {
            newNodesById[sibKey] = { ...sibNode, pos: sibPos };
            rebalanceWrites.push({
              childId: sibId,
              parentId: newParentId,
              pos: sibPos,
              expanded: sibNode.expanded,
              kind: (sibNode.kind || 'canonical') as 'canonical' | 'alias',
            });
          }
        });
        log.warn('[DataspaceTreeStore] ⚖️ Rebalanced', ordered.length, 'sibling positions under', newParentId ? newParentId.slice(-4) : 'ROOT');
      }

      if (!newPos) newPos = generatePositionBetween(null, null);

      const updatedNode = { ...node, parentId: newParentId, pos: newPos };
      newNodesById[isSameParent ? correctOldEdgeKey : correctNewEdgeKey] = updatedNode;

      moveData = { childId: node.id, oldParentId: node.parentId, newParentId, pos: newPos, expanded: node.expanded, kind: node.kind || 'canonical' };
      success = true;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (success && moveData) {
      get()._rebuildChildrenMap();
      log.log('[DataspaceTreeStore] ✅ moveNode optimistic update applied');

      // Route the move through the backend API (single source of truth).
      // The backend validates (cycle check, self-parent), writes to MongoDB,
      // and broadcasts the canonical change to the org Y.Doc so all clients
      // (including this one) converge on the correct authoritative state.
      const { childId, oldParentId, newParentId: newPId, pos } = moveData;
      const { dataspaceId } = get();

      const siblingUpdates = rebalanceWrites.map(w => ({
        childId: w.childId,
        parentId: w.parentId,
        pos: w.pos,
      }));

      return fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/move`, {
          method: 'POST',
          body: JSON.stringify({
            childId,
            newParentId: newPId,
            pos,
            siblingUpdates: siblingUpdates.length > 0 ? siblingUpdates : undefined,
          }),
        })
        .then(() => {
          log.log('[DataspaceTreeStore] ✅ moveNode persisted via API');
          return true;
        })
        .catch((err: unknown) => {
          console.error('[DataspaceTreeStore] moveNode API call failed — reverting optimistic update:', err);
          // Revert: restore the pre-move snapshot (captured before the optimistic set()).
          set({ nodesById: preMoveSnapshot, rebuildCount: get().rebuildCount + 1 });
          get()._rebuildChildrenMap();
          return false;
        });
    }

    log.error('[DataspaceTreeStore] moveNode failed — node not found');
    return Promise.resolve(false);
  },

  moveNodes: (inputs) => {
    if (inputs.length === 0) return Promise.resolve(true);
    if (inputs.length === 1) {
      const i = inputs[0];
      return get().moveNode(i.childId, i.newParentId, i.afterNodeId, i.fromParentId);
    }

    const preMoveSnapshot = { ...get().nodesById };
    const { dataspaceId } = get();
    if (!dataspaceId) return Promise.resolve(false);

    // Sequential position computation on a working copy of nodesById.
    // After each entity is placed we update the working copy so that subsequent
    // entities can reference the just-placed entity as their afterNodeId and
    // land at correct positions (prevents the before/after reversal bug).
    const workingNodes: Record<string, TreeNode> = { ...preMoveSnapshot };
    const notYetPlaced = new Set(inputs.map(i => i.childId));

    type ResolvedMove = {
      childId: string
      oldEdgeKey: string
      newEdgeKey: string
      newParentId: string | null
      pos: string
      node: TreeNode
      siblingUpdates: Array<{ childId: string; parentId: string | null; pos: string }>
    }

    const resolved: ResolvedMove[] = [];

    for (const input of inputs) {
      const { childId, newParentId: newPId, afterNodeId, fromParentId } = input;
      notYetPlaced.delete(childId);

      if (newPId !== null && newPId === childId) continue;

      // Prefer the caller-pinned edge (alias-aware). If that key is missing —
      // common at Drive's transparent root where currentFolderId is null but
      // the real parent is a Space / dataspace id — fall back to any edge for
      // this child. Skipping silently here is what made multi-select drops
      // look like "nothing happened" for some items.
      let oldEdgeKey =
        fromParentId !== undefined
          ? getEdgeKey(childId, fromParentId)
          : undefined;
      if (!oldEdgeKey || !workingNodes[oldEdgeKey]) {
        oldEdgeKey = Object.keys(workingNodes).find(k => workingNodes[k]?.id === childId);
      }
      if (!oldEdgeKey || !workingNodes[oldEdgeKey]) continue;

      const node = workingNodes[oldEdgeKey];
      const oldParentId = node.parentId;
      const correctOldEdgeKey = getEdgeKey(childId, oldParentId);
      const newEdgeKey = getEdgeKey(childId, newPId);

      // Extended siblings: stable siblings + already-placed batch entities at newPId.
      // Excludes the current entity and not-yet-placed batch entities so the
      // position sequence is computed correctly across the whole batch.
      const extendedSiblings = Object.values(workingNodes)
        .filter(n => n.parentId === newPId && n.id !== childId && !notYetPlaced.has(n.id))
        .sort((a, b) => a.pos.localeCompare(b.pos));

      const afterIndex = afterNodeId
        ? extendedSiblings.findIndex(n => n.id === afterNodeId)
        : -1;
      const insertIndex = afterIndex === -1 ? 0 : afterIndex + 1;
      const prevPos = insertIndex > 0 ? extendedSiblings[insertIndex - 1].pos : null;
      const nextPos = insertIndex < extendedSiblings.length ? extendedSiblings[insertIndex].pos : null;

      let newPos = generatePositionBetween(prevPos, nextPos);
      const siblingUpdates: ResolvedMove['siblingUpdates'] = [];

      const strictlyBetween =
        (prevPos === null || newPos.localeCompare(prevPos) > 0) &&
        (nextPos === null || newPos.localeCompare(nextPos) < 0);

      if (!strictlyBetween) {
        const orderedIds = [
          ...extendedSiblings.slice(0, insertIndex).map(n => n.id),
          childId,
          ...extendedSiblings.slice(insertIndex).map(n => n.id),
        ];
        const spaced = generateSpacedPositions(orderedIds.length);
        orderedIds.forEach((id, idx) => {
          if (id === childId) {
            newPos = spaced[idx];
          } else {
            const sibNode = workingNodes[getEdgeKey(id, newPId)];
            if (sibNode) siblingUpdates.push({ childId: id, parentId: newPId, pos: spaced[idx] });
          }
        });
      }

      resolved.push({ childId, oldEdgeKey: correctOldEdgeKey, newEdgeKey, newParentId: newPId, pos: newPos, node, siblingUpdates });

      // Update working state so subsequent entities see the correct sibling order
      delete workingNodes[correctOldEdgeKey];
      workingNodes[newEdgeKey] = { ...node, parentId: newPId, pos: newPos };
      for (const sib of siblingUpdates) {
        const k = getEdgeKey(sib.childId, sib.parentId);
        if (workingNodes[k]) workingNodes[k] = { ...workingNodes[k], pos: sib.pos };
      }
    }

    if (resolved.length === 0) return Promise.resolve(false);

    // Apply all optimistic updates in one set() / one rebuild
    set(state => {
      const newNodesById = { ...state.nodesById };
      for (const m of resolved) {
        if (m.oldEdgeKey !== m.newEdgeKey) delete newNodesById[m.oldEdgeKey];
        newNodesById[m.newEdgeKey] = { ...m.node, parentId: m.newParentId, pos: m.pos };
        for (const sib of m.siblingUpdates) {
          const k = getEdgeKey(sib.childId, sib.parentId);
          if (newNodesById[k]) newNodesById[k] = { ...newNodesById[k], pos: sib.pos };
        }
      }
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });
    get()._rebuildChildrenMap();

    // Single batch API call — one broadcast, all-or-nothing
    return fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/move-batch`, {
        method: 'POST',
        body: JSON.stringify({
          moves: resolved.map(m => ({
            childId: m.childId,
            newParentId: m.newParentId,
            pos: m.pos,
            siblingUpdates: m.siblingUpdates.length > 0 ? m.siblingUpdates : undefined,
          })),
        }),
      })
      .then(() => {
        log.log(`[DataspaceTreeStore] ✅ moveNodes: ${resolved.length} moves persisted`);
        return true;
      })
      .catch((err: unknown) => {
        console.error('[DataspaceTreeStore] moveNodes API call failed — rolling back:', err);
        set({ nodesById: preMoveSnapshot, rebuildCount: get().rebuildCount + 1 });
        get()._rebuildChildrenMap();
        return false;
      });
  },

  toggleExpanded: (nodeId: string) => {
    // expanded state does not affect childrenByParentId or sort order — no rebuild needed
    let expandedAfterToggle = false;
    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      if (matchingEdgeKeys.length === 0) {
        log.warn('[DataspaceTreeStore] toggleExpanded: no matching edges for nodeId:', nodeId);
        return state;
      }
      const newNodesById = { ...nodesById };
      let hasChanges = false;
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        const newExpanded = !node.expanded;
        newNodesById[edgeKey] = { ...node, expanded: newExpanded };
        _setExpandedLocal(node.id, newExpanded);
        expandedAfterToggle = newExpanded;
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });
    if (expandedAfterToggle) get().ensureFolderChildrenLoaded(nodeId);
  },

  expandNode: (nodeId: string) => {
    // expanded state does not affect childrenByParentId or sort order — no rebuild needed
    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      const newNodesById = { ...nodesById };
      let hasChanges = false;
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: true };
        _setExpandedLocal(node.id, true);
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });
    get().ensureFolderChildrenLoaded(nodeId);
  },

  ensureFolderChildrenLoaded: async (nodeId: string) => {
    const { treeFullyLoaded, loadedChildrenParents, pendingFullEdges } = get();
    if (treeFullyLoaded || !pendingFullEdges) return;
    if (loadedChildrenParents.has(nodeId)) return;

    const childEdges = pendingFullEdges.filter(e => e.parentId === nodeId);
    // Mark loaded even when empty — an empty folder shouldn't be re-scanned on every expand.
    set(state => ({ loadedChildrenParents: new Set(state.loadedChildrenParents).add(nodeId) }));
    if (childEdges.length === 0) return;

    const adds: [string, TreeNode][] = childEdges.map(edge => [
      getEdgeKey(edge.childId, edge.parentId),
      {
        id: edge.childId,
        parentId: edge.parentId,
        pos: edge.pos,
        expanded: _isExpandedLocal(edge.childId),
        kind: edge.kind,
      },
    ]);
    get()._applyEdgeBatch(adds, []);
    log.log(`[DataspaceTreeStore] 🌿 Lazily loaded ${adds.length} children for folder`, nodeId.slice(-8));
  },

  ensureNodeChainLoaded: (nodeId: string) => {
    const { treeFullyLoaded, pendingFullEdges } = get();
    if (treeFullyLoaded || !pendingFullEdges || pendingFullEdges.length === 0) return;

    const toMerge: DataspaceEdge[] = [];
    const visited = new Set<string>();
    let currentId: string | null = nodeId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      if (get().getNode(currentId)) break; // already mounted — chain above it is too
      const edge = pendingFullEdges.find(e => e.childId === currentId);
      if (!edge) break; // not deferred either — truly missing from the tree
      toMerge.push(edge);
      currentId = edge.parentId;
    }

    if (toMerge.length === 0) return;

    const adds: [string, TreeNode][] = toMerge.map(edge => [
      getEdgeKey(edge.childId, edge.parentId),
      {
        id: edge.childId,
        parentId: edge.parentId,
        pos: edge.pos,
        expanded: _isExpandedLocal(edge.childId),
        kind: edge.kind,
      },
    ]);
    get()._applyEdgeBatch(adds, []);
    log.log(`[DataspaceTreeStore] 🔗 Merged ${adds.length} deferred ancestor edge(s) for`, nodeId.slice(-8));
  },

  collapseNode: (nodeId: string) => {
    // expanded state does not affect childrenByParentId or sort order — no rebuild needed
    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      const newNodesById = { ...nodesById };
      let hasChanges = false;
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: false };
        _setExpandedLocal(node.id, false);
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });
  },

  expandAncestors: (nodeId: string) => {
    const { nodesById } = get();
    const matchingNodes = Object.values(nodesById).filter(n => n.id === nodeId);
    for (const node of matchingNodes) {
      get().expandNode(node.id);
      if (node.parentId) get().expandAncestors(node.parentId);
    }
  },

  expandAllNodes: (): { count: number } => {
    // "Expand all" means the user wants the complete tree visible — merge in
    // every deferred edge up front rather than lazily per-folder.
    const { treeFullyLoaded, pendingFullEdges } = get();
    if (!treeFullyLoaded && pendingFullEdges) {
      const adds: [string, TreeNode][] = pendingFullEdges.map(edge => [
        getEdgeKey(edge.childId, edge.parentId),
        {
          id: edge.childId,
          parentId: edge.parentId,
          pos: edge.pos,
          expanded: _isExpandedLocal(edge.childId),
          kind: edge.kind,
        },
      ]);
      get()._applyEdgeBatch(adds, []);
      set(state => {
        const allParents = new Set(state.loadedChildrenParents);
        pendingFullEdges.forEach(e => allParents.add(e.parentId as string));
        return { loadedChildrenParents: allParents };
      });
    }

    let count = 0;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: true };
        _setExpandedLocal(node.id, true);
        count++;
      });
      if (count === 0) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    log.log(`[DataspaceTreeStore] expandAllNodes: expanded ${count} nodes`);
    return { count };
  },

  collapseAllNodes: (): { count: number } => {
    let count = 0;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: false };
        _setExpandedLocal(node.id, false);
        count++;
      });
      if (count === 0) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    log.log(`[DataspaceTreeStore] collapseAllNodes: collapsed ${count} nodes`);
    return { count };
  },

  addAliasEdge: async (entityId: string, parentId: string | null) => {
    const { initialized, dataspaceId } = get();
    if (!initialized || !dataspaceId) { log.error('[addAliasEdge] Tree not initialized'); return; }

    const edgeKey = getEdgeKey(entityId, parentId);
    if (get().nodesById[edgeKey]) { log.log('[addAliasEdge] Edge already exists:', edgeKey); return; }

    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingPos = siblings.length > 0 ? get().nodesById[getEdgeKey(siblings[siblings.length - 1], parentId)]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);

    const newNode: TreeNode = { id: entityId, parentId, pos, expanded: false, kind: 'alias' };
    get()._applyAddNode(edgeKey, newNode);

    // Persist via backend so the edge lands in MongoDB (single source of truth)
    // and is broadcast to all connected clients. The backend broadcasts to the
    // org Y.Doc; do NOT write the Y.Doc directly from the frontend.
    fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges`, {
        method: 'POST',
        body: JSON.stringify({
          childId: entityId,
          parentId,
          pos,
          expanded: false,
          kind: 'alias',
        }),
      })
      .then(() => {
        log.log('[addAliasEdge] ✅ Alias edge persisted via API:', edgeKey);
      })
      .catch((err: unknown) => {
        console.warn('[addAliasEdge] API edge creation failed — reverting optimistic add:', err);
        get()._applyRemoveNode(edgeKey);
      });
  },

  ensureNodeExists: (
    nodeId: string,
    parentId: string | null,
    kind: 'canonical' | 'alias' = 'canonical',
    options: { skipBackendPersist?: boolean } = {}
  ) => {
    const { nodesById, initialized, dataspaceId } = get();
    if (!initialized || !dataspaceId) {
      log.log('[DataspaceTreeStore] Tree not initialized, skipping node creation');
      return;
    }
    if (nodeId === parentId) {
      log.error('[DataspaceTreeStore] ensureNodeExists: nodeId === parentId would create a self-loop, rejecting:', nodeId?.slice(-8));
      return;
    }
    const edgeKey = getEdgeKey(nodeId, parentId);
    if (nodesById[edgeKey]) return;

    // Validate parentId: if non-null, it must be a known node in this tree OR the
    // dataspaceId itself (Space root). Reject bogus parentIds to prevent ghost edges
    // that would persist to MongoDB via the Y.Doc save path.
    if (parentId !== null) {
      const parentExists = Object.values(nodesById).some(n => n.id === parentId);
      if (!parentExists && parentId !== dataspaceId) {
        log.warn('[DataspaceTreeStore] ensureNodeExists: parentId not found in tree:', parentId?.slice(-8));
        return;
      }
    }

    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingPos = siblings.length > 0 ? nodesById[getEdgeKey(siblings[siblings.length - 1], parentId)]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);
    const newNode: TreeNode = { id: nodeId, parentId, pos, expanded: false, kind };

    // Apply optimistically to the local tree so the node is immediately visible.
    get()._applyAddNode(edgeKey, newNode);

    // skipBackendPersist: the caller already knows the backend created this exact
    // edge as part of another operation (e.g. entityActions.createEntity's
    // AUTO-EDGE mirror — see services/entity_creator.py's ensure_entity_has_edge,
    // which is mandatory/strict for every displayable entity type). POSTing again
    // here would be a redundant round trip against the same DocumentDB collection
    // for an edge that's already guaranteed to exist; the org Y.Doc broadcast will
    // reconcile pos/kind if the frontend's optimistic guess ever diverges.
    if (options.skipBackendPersist) return;

    // Route the edge creation through the backend API so it lands in MongoDB
    // (single source of truth) and is broadcast to all connected clients.
    fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges`, {
        method: 'POST',
        body: JSON.stringify({
          childId: nodeId,
          parentId,
          pos,
          expanded: false,
          kind,
        }),
      })
      .catch((err: unknown) => {
        console.warn('[DataspaceTreeStore] ensureNodeExists: API edge creation failed — reverting optimistic add:', err);
        get()._applyRemoveNode(edgeKey);
      });
  },
  
  // ========================================================================
  // Queries (O(1) lookups)
  // ========================================================================
  
  getRootNodeIds: () => {
    const { childrenByParentId } = get();
    return childrenByParentId.get(null) || [];
  },
  
  getChildNodeIds: (parentId: string | null) => {
    const { childrenByParentId } = get();
    return childrenByParentId.get(parentId) || [];
  },

  getDescendantNodeIds: (parentId: string) => {
    const { childrenByParentId } = get();
    const result: string[] = [];
    const visited = new Set<string>();
    const queue: string[] = [parentId];
    // parentId itself is the root — exclude it from the result.
    visited.add(parentId);
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const children = childrenByParentId.get(currentId) || [];
      for (const childId of children) {
        if (visited.has(childId)) continue;
        visited.add(childId);
        result.push(childId);
        queue.push(childId);
      }
    }
    return result;
  },

  getNode: (nodeId: string, parentId?: string | null) => {
    // ✅ ALIAS SUPPORT: If parentId provided, look up specific edge; otherwise
    // prefer the canonical edge over any alias edges. This prevents drag/drop
    // and move flows from accidentally targeting an alias when the user
    // intended the canonical location.
    if (parentId !== undefined) {
      const edgeKey = getEdgeKey(nodeId, parentId);
      return get().nodesById[edgeKey];
    }
    const nodesById = get().nodesById;
    const matches = Object.values(nodesById).filter(n => n.id === nodeId);
    if (matches.length === 0) return undefined;
    return matches.find(n => (n.kind || 'canonical') === 'canonical') ?? matches[0];
  },
  
  // ========================================================================
  // Safe Accessor Methods - Prevent nodesById Key Format Bugs
  // ========================================================================
  
  /**
   * Get a node by edge (childId + parentId)
   * ✅ SAFE: Encapsulates edge key format
   */
  getNodeByEdgeKey: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return get().nodesById[edgeKey];
  },
  
  /**
   * Get all nodes (edges) for an entity (supports aliases - same entity in multiple locations)
   * ✅ SAFE: Returns all edges, not just first one
   */
  getNodesByEntityId: (entityId: string) => {
    const { nodesById } = get();
    return Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
  },
  
  /**
   * Check if a specific edge exists
   * ✅ SAFE: Encapsulates edge key format
   */
  hasEdge: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return !!get().nodesById[edgeKey];
  },
  
  /**
   * Get expanded state for an entity
   * ✅ SAFE: Handles multiple edges (returns true if ANY edge is expanded)
   */
  getExpandedState: (entityId: string) => {
    const { nodesById } = get();
    const matchingNodes = Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
    return matchingNodes.some((node: TreeNode) => node.expanded);
  },
  
  // ========================================================================
  // Legacy Methods (for backward compatibility)
  // ========================================================================
  
  isExpanded: (nodeId: string, parentId?: string | null) => {
    const node = get().getNode(nodeId, parentId);
    return node?.expanded ?? false;
  },
  
  hasChildren: (nodeId: string) => {
    return get().getChildNodeIds(nodeId).length > 0;
  },

  isEntityInSubtree: (entityId: string, ancestorId: string) => {
    const { nodesById } = get();
    const entityEdges = Object.values(nodesById).filter(n => n.id === entityId);
    for (const edge of entityEdges) {
      let currentParentId: string | null = edge.parentId;
      const visited = new Set<string>();
      while (currentParentId !== null) {
        if (currentParentId === ancestorId) return true;
        if (visited.has(currentParentId)) break;
        visited.add(currentParentId);
        // Walk up via the canonical edge for this parent (skip aliases to avoid loops)
        const parentNode = Object.values(nodesById).find(
          n => n.id === currentParentId && n.kind !== 'alias'
        );
        if (!parentNode) break;
        currentParentId = parentNode.parentId;
      }
    }
    return false;
  },

  // ========================================================================
  // Internal Mutations
  // ========================================================================
  
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => {
    if (!operation) {
      log.log(`[TreeStore] _applyAddNode (remote): key=${edgeKey.slice(-16)} child=${node.id.slice(-8)} parent=${node.parentId?.slice(-8) ?? 'ROOT'}`)
    }
    const nodeWithLocalExpanded = { ...node, expanded: _isExpandedLocal(node.id) };
    set(state => ({
      nodesById: { ...state.nodesById, [edgeKey]: nodeWithLocalExpanded },
      rebuildCount: state.rebuildCount + 1,
    }));
    get()._rebuildChildrenMap();
  },

  _applyRemoveNode: (edgeKey: string) => {
    const node = get().nodesById[edgeKey];
    if (!node) {
      log.warn('[DataspaceTreeStore] _applyRemoveNode: node not found for key:', edgeKey.slice(-12));
      return;
    }
    log.log('[DataspaceTreeStore] 🗑️ Removing edge:', { edgeKey: edgeKey.slice(-16), nodeId: node.id.slice(-8) });

    set(state => {
      if (!state.nodesById[edgeKey]) return state;
      const newNodesById = { ...state.nodesById };
      delete newNodesById[edgeKey];
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });

    get()._rebuildChildrenMap();
  },

  /**
   * Batched edge removal — one set() / one rebuild for N edges. Used by
   * bulk-delete flows in the Space sidebar to avoid the N×rebuild + N×render
   * thrash of per-item _applyRemoveNode calls.
   */
  _applyRemoveNodes: (edgeKeys: string[]) => {
    if (!edgeKeys || edgeKeys.length === 0) return;

    set(state => {
      const newNodesById = { ...state.nodesById };
      let removed = 0;
      for (const edgeKey of edgeKeys) {
        if (newNodesById[edgeKey]) {
          delete newNodesById[edgeKey];
          removed++;
        }
      }
      if (removed === 0) return state;
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });

    get()._rebuildChildrenMap();

    log.log('[DataspaceTreeStore] 🗑️ Bulk-removed edges:', { requested: edgeKeys.length });
  },

  /**
   * Restore edges previously removed by _applyRemoveNodes — local nodesById
   * only. The backend's authoritative broadcast (from the failed/denied delete
   * response) reconciles the org Y.Doc; the frontend just reverts its local
   * optimistic state so the tree isn't left in a stale "deleted" visual state.
   */
  _restoreRemovedNodes: (entries: Array<[string, TreeNode]>) => {
    if (!entries || entries.length === 0) return;

    set(state => {
      const newNodesById = { ...state.nodesById };
      for (const [edgeKey, node] of entries) {
        newNodesById[edgeKey] = { ...node, expanded: _isExpandedLocal(node.id) };
      }
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });
    get()._rebuildChildrenMap();

    log.log('[DataspaceTreeStore] ♻️  Restored edges after failed bulk delete:', {
      count: entries.length,
    });
  },
  
  _applyEdgeBatch: (adds: [string, TreeNode][], removes: string[]) => {
    if (adds.length === 0 && removes.length === 0) return;
    set(state => {
      const newNodesById = { ...state.nodesById };
      for (const edgeKey of removes) {
        delete newNodesById[edgeKey];
      }
      for (const [edgeKey, node] of adds) {
        newNodesById[edgeKey] = { ...node, expanded: _isExpandedLocal(node.id) };
      }
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });
    get()._rebuildChildrenMap();
  },

  _rebuildChildrenMap: () => {
    const startTime = performance.now();
    const { nodesById } = get();
    const newMap = new Map<string | null, string[]>();

    // Keyed by edgeKey (parentId:entityId) so alias edges with different parents never collide.
    const posMap = new Map<string, string>();

    // Build the parent→children map. Use a Set per parent to deduplicate in O(1) per insert,
    // then convert to arrays — avoids the O(N²) spread from repeated [...existing, id] calls.
    const childSets = new Map<string | null, Set<string>>();

    const selfReferenceIds = new Set<string>();

    Object.entries(nodesById).forEach(([edgeKey, node]) => {
      // Self-referencing edges are always corrupt. Skip them from the tree and
      // schedule a Y.Doc deletion so they stop firing on every rebuild.
      if (node.id === node.parentId) {
        selfReferenceIds.add(node.id);
        return;
      }
      posMap.set(edgeKey, node.pos);
      let s = childSets.get(node.parentId);
      if (!s) {
        s = new Set<string>();
        childSets.set(node.parentId, s);
      }
      s.add(node.id);
    });

    if (selfReferenceIds.size > 0) {
      console.error('[DataspaceTreeStore] ❌ Self-referencing edge(s) found — requesting server reseed:', [...selfReferenceIds]);
      _scheduleOrgReseed(get().dataspaceId);
    }

    childSets.forEach((childSet, parentId) => {
      newMap.set(parentId, Array.from(childSet));
    });

    // Cycle detection — runs in all environments to prevent infinite rendering.
    // SAFETY: when a cycle is detected we suppress it from the in-memory render
    // map only.  We do NOT delete from the shared org Y.Doc — doing so is
    // non-deterministic (DFS traversal order determines which edge is evicted)
    // and can accidentally remove the *real* edge, making an item unreachable.
    // Instead we request a server reseed, which rebuilds the Y.Map from the
    // authoritative MongoDB state.
    {
      const visited = new Set<string>();
      const inStack = new Set<string>();
      const cycleEdges = new Set<string>();
      const dfs = (id: string): boolean => {
        if (inStack.has(id)) return true;
        if (visited.has(id)) return false;
        visited.add(id);
        inStack.add(id);
        for (const childId of newMap.get(id) || []) {
          if (dfs(childId)) {
            const parentChildren = newMap.get(id);
            if (parentChildren) {
              newMap.set(id, parentChildren.filter(c => c !== childId));
            }
            cycleEdges.add(`${id}→${childId}`);
          }
        }
        inStack.delete(id);
        return false;
      };
      for (const children of newMap.values()) {
        for (const id of children) {
          if (!visited.has(id)) dfs(id);
        }
      }
      if (cycleEdges.size > 0) {
        console.error('[DataspaceTreeStore] ❌ Cycle(s) detected — suppressed from render, requesting server reseed:', [...cycleEdges]);
        _scheduleOrgReseed(get().dataspaceId);
      }
    }

    // Sort each parent's children by fractional position.
    newMap.forEach((children, parentId) => {
      children.sort((a, b) => {
        const posA = posMap.get(getEdgeKey(a, parentId)) || '';
        const posB = posMap.get(getEdgeKey(b, parentId)) || '';
        return posA.localeCompare(posB);
      });
    });

    const elapsed = performance.now() - startTime;

    if (VERBOSE_LOGGING) {
      log.log('[_rebuildChildrenMap] 🔄 Rebuilt children map:', {
        totalParents: newMap.size,
        totalEdges: Object.keys(nodesById).length,
        rebuildTime: `${elapsed.toFixed(2)}ms`,
      });
    }

    if (elapsed > 10) {
      log.warn(`[DataspaceTreeStore] ⚠️ Slow rebuild: ${elapsed.toFixed(2)}ms for ${Object.keys(nodesById).length} nodes`);
    }

    set({ childrenByParentId: newMap });
  },
  
  // ========================================================================
  // Persistence: all edge writes go through the backend REST API.
  // MongoDB is the single source of truth; the org Y.Doc edges map is a
  // read-only projection maintained by the WS server. The frontend never
  // writes the org Y.Doc edges map directly.
  // ========================================================================
  
  // ========================================================================
  // Cleanup
  // ========================================================================

  cleanup: () => {
    const { entityRegistryUnsubscribe, entityEventUnsubscribe } = get();
    if (entityRegistryUnsubscribe) entityRegistryUnsubscribe();
    if (entityEventUnsubscribe) entityEventUnsubscribe();
    set({
      dataspaceId: null,
      nodesById: {},
      childrenByParentId: new Map(),
      initialized: false,
      rebuildCount: 0,
      entityRegistryUnsubscribe: null,
      entityEventUnsubscribe: null,
      treeFullyLoaded: true,
      loadedChildrenParents: new Set(),
      pendingFullEdges: null,
    });
  },
  
  // ========================================================================
  // Debug
  // ========================================================================
  
  debugPrintTree: () => {
    const { nodesById, getRootNodeIds, getChildNodeIds } = get();
    log.log('\n🌲 DATASPACE TREE:');
    
    const printNode = (nodeId: string, depth: number = 0) => {
      const node = nodesById[nodeId];
      const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((e: BaseEntityType) => e._id === nodeId);
      const indent = '  '.repeat(depth);
      const icon = node?.expanded ? '📂' : '📁';
      
      log.log(`${indent}${icon} ${entity?.name || 'Unknown'} (${node?.pos || 'no-pos'}) [${nodeId.slice(-4)}]`);
      
      if (node?.expanded) {
        const children = getChildNodeIds(nodeId);
        children.forEach(childId => printNode(childId, depth + 1));
      }
    };
    
    const roots = getRootNodeIds();
    log.log(`Root level (${roots.length} items):`);
    roots.forEach((id: string) => printNode(id, 0));
    log.log('\n');
  }
}));

export default useDataspaceTreeStore;

// No beforeunload handler needed — Y.Doc writes are synchronously committed to
// the WebSocket provider's pending-updates queue. The server's debounced writeState
// flushes to MongoDB when the connection closes (last-client-disconnect handler).
